import { Fragment, ReactElement } from 'react'
import { OrderPart } from '../../../../../../../../../src/api/cart/interfaces'
import styled from 'styled-components'
import { I18n, Translate2 } from 'common'
import { Price } from '../../../../../../../../../src/ui-components'
import { IBundleProduct } from 'libs/cart/src/store/ISellerCart'
import { useUiStateStore } from '../../../../../../../../../src/store/uiState/UiStateStore'
import { BundledTag } from 'src/features/cart/components/BundledTag'
import {
  SectionTitle,
  ReceiptTableGrid,
  ReceiptCell,
  ReceiptDivider,
} from '../../../printStyles'

interface Props {
  parts: OrderPart[]
  showYourCost: boolean
}

const Parts = (props: Props): ReactElement => {
  const { parts, showYourCost } = props
  const uiStore = useUiStateStore()

  const gridColumns = showYourCost
    ? '2.2fr 0.7fr 0.7fr 0.4fr 0.7fr'
    : '2.5fr 0.8fr 0.4fr 0.7fr'

  const calcCostTotal = (p: OrderPart) => {
    const part = p as IBundleProduct
    const baseTotal =
      ((p.unitCorePrice ?? 0) + (p.unitCostPrice ?? 0)) *
      (p.quantityShipped ?? 0)
    if (uiStore?.showBundles() && part.bundled) {
      return (
        ((part.bundlePrice ?? 0) + (p.unitCorePrice ?? 0)) *
        (p.quantityShipped ?? 0)
      )
    }
    return baseTotal
  }

  const calcListTotal = (p: OrderPart) =>
    ((p.unitListPrice ?? 0) + (p.unitCorePrice ?? 0)) * (p.quantityShipped ?? 0)

  const renderCoreFee = (unitCorePrice: number | undefined) => {
    if ((unitCorePrice ?? 0) <= 0) return null
    return (
      <CoreMeta>
        <I18n name="core" />: <Price num={unitCorePrice} />
      </CoreMeta>
    )
  }

  return (
    <>
      <SectionTitle>{Translate2('item')}</SectionTitle>
      <ReceiptTableGrid $columns={gridColumns}>
        <ReceiptCell $header $align="start">
          {Translate2('item')}
        </ReceiptCell>
        {showYourCost && (
          <ReceiptCell $header $align="end">
            {Translate2('yourCost')}
          </ReceiptCell>
        )}
        <ReceiptCell $header $align="end">
          {Translate2('listPrice')}
        </ReceiptCell>
        <ReceiptCell $header $align="end">
          {Translate2('qty')}
        </ReceiptCell>
        <ReceiptCell $header $align="end">
          {Translate2('total')}
        </ReceiptCell>

        {parts.map((p) => (
          <Fragment key={`${p.partNumber}_${p.lineCode}`}>
            <ReceiptCell $align="start">
              <PartDescription>{p.description}</PartDescription>
              <PartMeta>
                <I18n name="lineCode" />: {p.lineCode}
                <ReceiptDivider>|</ReceiptDivider>
                <I18n name="Part#" />: {p.partNumber}
              </PartMeta>
            </ReceiptCell>

            {showYourCost && (
              <ReceiptCell $align="end">
                <PriceWrapper>
                  <Price num={p.unitCostPrice} />
                  {uiStore?.showBundles() && (p as IBundleProduct).bundled && (
                    <ReceiptBundledTagWrapper>
                      <BundledTag />
                    </ReceiptBundledTagWrapper>
                  )}
                  {renderCoreFee(p.unitCorePrice)}
                </PriceWrapper>
              </ReceiptCell>
            )}

            <ReceiptCell $align="end">
              <Price num={p.unitListPrice} />
              {!showYourCost && renderCoreFee(p.unitCorePrice)}
            </ReceiptCell>

            <ReceiptCell $align="end">{p.quantityShipped}</ReceiptCell>

            <ReceiptCell $align="end">
              {showYourCost ? (
                (p as IBundleProduct).bundled && uiStore?.showBundles() ? (
                  <>
                    <BoldText>
                      <Price num={calcCostTotal(p)} />
                    </BoldText>
                    <StrikeText>
                      <Price
                        num={
                          ((p.unitCostPrice ?? 0) + (p.unitCorePrice ?? 0)) *
                          (p.quantityShipped ?? 0)
                        }
                      />
                    </StrikeText>
                  </>
                ) : (
                  <Price num={calcCostTotal(p)} />
                )
              ) : (
                <Price num={calcListTotal(p)} />
              )}
            </ReceiptCell>
          </Fragment>
        ))}
      </ReceiptTableGrid>
    </>
  )
}

const PartDescription = styled.span`
  font-weight: 400;
`

const PartMeta = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 11pt;
  line-height: 14pt;
  gap: 2px;
`

const CoreMeta = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-size: 11pt;
  line-height: 14pt;
  gap: 2px;
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const BoldText = styled.span`
  font-weight: 700;
`

const StrikeText = styled.span`
  text-decoration: line-through;
  font-size: 11pt;
`

const ReceiptBundledTagWrapper = styled.div`
  transform: scale(0.85);
  transform-origin: right;
  margin-top: -2px;
  margin-bottom: -2px;
`

export default Parts
